<template>
    <div>
        <div v-if="disableLayout === false"
             class="reports-page"
        >
            <layout-primary :title-text="reportTitle"
                            :disable-spinner="disableSpinner"
            >
                <div :class="{'tooltip-overlay':showToolTip}"
                     :title="reportToolTip"
                />
                <div class="reports-page__container">
                    <div v-if="showReportTitle"
                         class="custom-report-title-header"
                    >
                        <div class="custom-report-title-section">
                            <span class="custom-report-title">
                                {{ reportTitle | addReportText }}
                            </span>

                            <div class="report-command-btn-section">
                                <button
                                    id="DownloadReportExcel"
                                    class="report-command-btn"
                                    value="Download"
                                    :title="downloadToolTip"
                                    :disabled="downloadDisabled"
                                    @click.prevent="downloadReportClick"
                                >
                                    Download
                                </button>
                            </div>
                        </div>
                    </div>
                    <div
                        v-show="!oneWarehouseSelected"
                        class="resizer"
                    >
                        <spinner :active="loading"
                                 class="report-spinner"
                        />
                        <div v-if="customLoader"
                             class="custom-loader-wrapper"
                        >
                            <div class="custom-loader" />
                        </div>
                        <section :id="`report-container-${reportType}`"
                                 class="section-class"
                                 :title="reportToolTip"
                        />
                    </div>
                    <div
                        v-show="oneWarehouseSelected"
                        class="one-warehouse-selected"
                    >
                        <img
                            src="/favicon/one-warehouse-selected-wh.svg"
                            alt="Only one warehouse selected"
                        >
                    </div>
                </div>
            </layout-primary>
        </div>
        <div v-else
             class="reports-page-custom"
        >
            <div :class="{'tooltip-overlay':showToolTip}"
                 :title="reportToolTip"
            />
            <div class="reports-page-custom__container">
                <div v-if="reportTitleVisible"
                     class="report-title-header"
                >
                    <p class="report-title">
                        {{ reportTitle }}
                    </p>
                </div>
                <div
                    v-show="!oneWarehouseSelected"
                    class="resizer"
                >
                    <spinner v-if="disableSpinner === false"
                             :active="loading"
                             class="report-spinner-custom"
                    />
                    <div v-if="customLoader"
                         class="custom-loader-wrapper"
                    >
                        <div class="custom-loader" />
                    </div>

                    <section :id="`report-container-${reportType}`"
                             class="section-class"
                             :title="reportToolTip"
                    />
                </div>
                <div
                    v-show="oneWarehouseSelected"
                    class="one-warehouse-selected"
                >
                    <img
                        src="/favicon/one-warehouse-selected-wh.svg"
                        alt="Only one warehouse selected"
                    >
                </div>
            </div>
        </div>
    </div>
</template>

<script>
// eslint-disable-next-line no-unused-vars
import * as pbi from 'powerbi-client';
import * as models from 'powerbi-models';
import constants from '@/core/constants';
import { ReportTypes } from '@/core/enums';
import Router from '@/router';
import LayoutPrimary from '@/components/layout/LayoutPrimary';
import Spinner from '@/components/Spinner';
import reportsApi from '@/services/resources/reportsApi';

export default {
    name: 'ReportSection',
    components: {
        LayoutPrimary,
        Spinner,
    },
    ReportTypes,
    filters: {
        addReportText(value) {
            return `${value} Report`;
        },
    },
    props: {
        reportType: {
            type: Number,
            required: true,
        },
        reportTitle: {
            type: String,
            required: true,
        },
        showReportTitle: {
            type: Boolean,
            default: false,
        },
        reportTitleVisible: {
            type: Boolean,
            default: false,
        },
        reportToolTip: {
            type: String,
            required: false,
            default: '',
        },
        disableLayout: {
            type: Boolean,
            default: false,
        },
        disableSpinner: {
            type: Boolean,
            default: false,
        },
        filterPaneEnabled: {
            type: Boolean,
            default: false,
        },
        navContentPaneEnabled: {
            type: Boolean,
            default: true,
        },
        embedFilters: {
            type: Array,
            default: () => [],
        },
        hidePowerBiLogo: {
            type: Boolean,
            default: false,
        },
        showCustomLoader: {
            type: Boolean,
            default: false,
        },
        visualHeader: {
            type: Boolean,
            default: true,
        },
        getUnderlyingReportData: {
            type: Boolean,
            default: false,
        },
        oneWarehouseSelected: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            loading: true,
            report: null,
            customLoader: false,
            styles: null,
            downloadToolTip: 'The Download button exports all data from the Fulfillment and Shipping Details grids into a file. In some cases, browser timeouts caused by internet latency may result in an empty export file. If this occurs, please reduce the selected time frame and try the download again.',
            curRowCount: 0,
            maxRowCount: 1000000,
            downloadDisabled: true,
        };
    },
    computed: {
        showToolTip() {
            if (this.reportToolTip) {
                return true;
            }
            return false;
        },
    },
    watch: {
        embedFilters: {
            async handler(newFilters) {
                if (this.report) {
                    await this.applyFilters(newFilters);
                }
            },
            deep: true,
        },
        showReportTitle: {
            async handler(value) {
                this.showReportTitle = value;
            },
            deep: true,
        },
    },
    async mounted() {
        const isEnabled = `${process.env.VUE_APP_POWERBI_REPORTS_ENABLED}`;
        if (isEnabled !== 'true') {
            Router.push('/');
        }

        if (this.showCustomLoader) {
            this.customLoader = true;
        }

        await this.loadEmbedInfo();
    },
    methods: {
        async loadEmbedInfo() {
            const embedParams = await this.$monitor(() => reportsApi.getEmbedInfo(this.reportType));

            if (!embedParams || !embedParams.embedToken || !embedParams.embedReport) {
                this.loading = false;
                // this.customLoader = false;
                return;
            }
            const reportContainer = document.getElementById(`report-container-${this.reportType}`);

            const reportLoadConfig = {
                type: 'report',
                tokenType: models.TokenType.Embed,
                accessToken: embedParams.embedToken.token,
                permissions: models.Permissions.Read,
                // You can embed different reports as per your need
                embedUrl: embedParams.embedReport[0].embedUrl,
                settings: {
                    filterPaneEnabled: this.filterPaneEnabled,
                    navContentPaneEnabled: this.navContentPaneEnabled,
                    visualizations: {
                        expanded: false,
                    },
                    layoutType: models.LayoutType.Custom,
                    customLayout: {
                        displayOption: models.DisplayOption.FitToPage,
                    },
                    visualSettings: {
                        visualHeaders: [
                            {
                                settings: {
                                    visible: this.visualHeader,
                                },
                            },
                        ],
                    },
                },

                // Enable this setting to remove gray shoulders from embedded report
                // settings: {
                //     background: models.BackgroundType.Transparent
                // }
            };

            // Use the token expiry to regenerate Embed token for seamless end user experience
            // Refer https://aka.ms/RefreshEmbedToken
            // const tokenExpiry = embedParams.embedToken.expiration;

            // Embed Power BI report when Access token and Embed URL are available
            // eslint-disable-next-line
            this.report = powerbi.embed(reportContainer, reportLoadConfig);


            this.report.on('rendered', async () => {
                this.loading = false;
                this.customLoader = false;
                this.report.off('rendered');
                setTimeout(async () => { await this.setCurRowCount(); }, 2000);
            });

            // Clear any other loaded handler events
            this.report.off('loaded');

            // Triggers when a report schema is successfully loaded
            this.report.on('loaded', async () => {
                await this.applyFilters(this.embedFilters);
                await this.applyDateRangeFilters();
                await this.setCurRowCount();
            });

            // covers basic select filters
            this.report.on('dataSelected', async () => {
                setTimeout(async () => { await this.setCurRowCount(); }, 2000);
            });

            // Listen for filter changes
            this.report.on('visualClicked', async () => {
                setTimeout(async () => { await this.setCurRowCount(); }, 2000);
            });

            // Clear any other error handler events
            this.report.off('error');

            // Handle embed errors
            this.report.on('error', (event) => {
                const errorMsg = event.detail;
                // eslint-disable-next-line no-alert
                alert(`bi loading error${errorMsg}`);
                this.loading = false;
                this.customLoader = false;
                this.$toast.error(errorMsg, { timeout: constants.domain.defaultToastTimeout });
            });
        },
        async applyFilters(filters) {
            try {
                const filterObjects = filters.map((filter) => {
                    if (filter.$schema === 'http://powerbi.com/product/schema#basic') {
                        return new models.BasicFilter(
                            filter.target,
                            filter.operator,
                            filter.values,
                        );
                    } if (filter.$schema === 'http://powerbi.com/product/schema#advanced') {
                        return new models.AdvancedFilter(
                            filter.target,
                            filter.logicalOperator,
                            filter.conditions,
                        );
                    }
                    return true;
                });

                const report = this.report;
                await report.updateFilters(models.FiltersOperations.ReplaceAll, filterObjects);
                return await this.getUnderlyingData();
            } catch (error) {
                this.$toast.error(`Error getting ${this.reportTitle} data`, {
                    timeout: constants.domain.defaultToastTimeout,
                });
            }
            return null;
        },
        async setCurRowCount() {
            const report = await this.report;
            const page = await report.getActivePage();
            const visuals = await page.getVisuals();
            const rowCountVisual = visuals.find(v => v.title === 'TotalRowCount');
            if (rowCountVisual !== null) {
                setTimeout(async () => {
                    const data = await rowCountVisual.exportData(models.ExportDataType.Summarized);
                    const rows = data.data.split('\r\n');
                    if (rows.length > 1) {
                        this.curRowCount = rows[1].replace(/['",]+/g, '');
                    } else {
                        this.curRowCount = 0;
                    }
                    if (parseInt(this.curRowCount, 10) < this.maxRowCount) {
                        this.downloadDisabled = false;
                        this.downloadToolTip = 'The Download button exports all data from the Fulfillment and Shipping Details grids into a file. In some cases, browser timeouts caused by internet latency may result in an empty export file. If this occurs, please reduce the selected time frame and try the download again.';
                    } else {
                        this.downloadDisabled = true;
                        this.downloadToolTip = 'This query exceeds Excel’s maximum row count and cannot be downloaded.  Please refine your search and try again.';
                    }
                }, 1000);
            } else {
                this.curRowCount = 0;
            }
        },
        async applyDateRangeFilters() {
            const pages = await this.report.getPages();
            const activePage = pages.find(page => page.isActive);
            const visuals = await activePage.getVisuals();
            const maxDateVisual = visuals.find(
                v => v.title === 'MaxDate',
            );
            if (maxDateVisual !== null) {
                const maxDateData = await maxDateVisual.exportData(models.ExportDataType.Summarized);
                const rows = maxDateData.data.split('\r\n');
                if (rows.length > 1) {
                    const endDateValue = rows[1].replaceAll(',', '').replaceAll('"', '');
                    const d = new Date(endDateValue);
                    const startDateValue = d.setMonth(d.getMonth() - 1);
                    const s = new Date(startDateValue);
                    const sValue = s.toISOString();
                    const d2 = new Date(endDateValue);
                    const finalEndDateValue = d2.setDate(d2.getDate() + 1);
                    const e = new Date(finalEndDateValue);
                    const eValue = e.toISOString();
                    const filterObjects = [];

                    if (this.reportType === ReportTypes.ShippedOrderCost) {
                        const advancedFilter = {
                            $schema: 'http://powerbi.com/product/schema#advanced',
                            target: {
                                table: 'XPT vwShippingOrdersCost',
                                column: 'Billing Date',
                            },
                            logicalOperator: 'And',
                            conditions: [
                                {
                                    operator: 'GreaterThanOrEqual',
                                    value: sValue,
                                },
                                {
                                    operator: 'LessThan',
                                    value: eValue,
                                },
                            ],
                            filterType: models.FilterType.Advanced,
                        };

                        const dateSlicer = visuals.find(visual => visual.title === 'date');
                        filterObjects.push(advancedFilter);
                        dateSlicer.setSlicerState({
                            filters: filterObjects,
                        });
                    }
                }
            }
        },
        async downloadReportClick() {
            let downloadURL = await this.$monitor(() => reportsApi.getReportDownloadFileURL());
            downloadURL = `${downloadURL}?reportType=${this.reportType}`;
            const fileName = await this.getDownloadFileName();
            downloadURL = `${downloadURL}&fileName=${fileName}`;
            const report = await this.report;
            const page = await report.getActivePage();
            const visuals = await page.getVisuals();
            const slicers = visuals.filter(visual => visual.type === 'slicer');
            let query = '';
            const minDateVisual = visuals.find(
                v => v.title === 'MinDate',
            );
            if (minDateVisual !== null) {
                const minDateData = await minDateVisual.exportData(models.ExportDataType.Summarized);
                const rows = minDateData.data.split('\r\n');
                if (rows.length > 1) {
                    const value = rows[1].replaceAll(',', '').replaceAll('"', '');
                    query = `${query}Z%20StartDate%7EEquals%7E${value}`;
                }
            }
            const maxDateVisual = visuals.find(
                v => v.title === 'MaxDate',
            );
            if (maxDateVisual !== null) {
                const maxDateData = await maxDateVisual.exportData(models.ExportDataType.Summarized);
                const rows = maxDateData.data.split('\r\n');
                if (rows.length > 1) {
                    const value = rows[1].replaceAll(',', '').replaceAll('"', '');
                    query = `${query}Z%20EndDate%7EEquals%7E${value}`;
                }
            }
            const promises = slicers.map(async (slicer) => {
                const slicerState = await slicer.getSlicerState();
                const curSlicerFilterJSON = JSON.stringify(slicerState.filters);
                if (curSlicerFilterJSON !== '[]') {
                    // alert(curSlicerFilterJSON);
                    const filter = slicerState.filters[0];
                    if (filter.filterType === 0) {
                        const conditions = filter.conditions;
                        conditions.forEach((element) => {
                            const columnName = filter.target.column;
                            const cleanColumnName = columnName.replace('#', 'Number');
                            query = `${query}Z%20${cleanColumnName}%7E${element.operator}%7E${element.value}`;
                        });
                    } else {
                        const columnName = filter.target.column;
                        const cleanColumnName = columnName.replace('#', 'Number');
                        query = `${query}Z%20${cleanColumnName}%7E${filter.operator}%7E${filter.values}`;
                    }
                }
            });
            await Promise.all(promises);
            if (query !== '') {
                downloadURL = `${downloadURL}&SearchQuery=${query}`;
            }
            const link = document.createElement('a');
            link.href = downloadURL;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },
        async getDownloadFileName() {
            const today = new Date();
            const year = today.getFullYear();
            const month = String(today.getMonth() + 1).padStart(2, '0');
            const day = String(today.getDate()).padStart(2, '0');
            const formattedDate = `${year}${month}${day}`;
            const downloadFileName = `${formattedDate}_${this.reportTitle}.csv`;
            return downloadFileName;
        },
        async getUnderlyingData() {
            if (this.getUnderlyingReportData) {
                try {
                    const report = await this.report;
                    const page = await report.getActivePage();
                    const visuals = await page.getVisuals();
                    // eslint-disable-next-line no-restricted-syntax
                    for (const visual of visuals) {
                        // eslint-disable-next-line no-await-in-loop
                        if (visual.type === 'columnChart') {
                            // eslint-disable-next-line no-await-in-loop
                            setTimeout(async () => {
                                const data = await visual.exportData(models.ExportDataType.Summarized);
                                // Check if there are any data rows (more than just headers)
                                // Split the data into rows
                                const rows = data.data.split('\r\n');
                                // Headers row + empty last row = 2, so we check for more than 2 rows
                                const hasData = rows.length > 2;
                                this.$emit('column-chart-data-status', hasData);
                            }, 1000);
                        }

                        if (visual.type === 'barChart') {
                            setTimeout(async () => {
                                // eslint-disable-next-line no-await-in-loop
                                const data = await visual.exportData(models.ExportDataType.Summarized);
                                // Split the data into rows
                                const rows = data.data.split('\r\n');
                                // Check if there are any data rows (more than just headers)
                                // Headers row + empty last row = 2, so we check for more than 2 rows
                                const hasData = rows.length > 2;
                                this.$emit('bar-chart-data-status', hasData);
                            }, 1000);
                        }

                        if (visual.type === 'pieChart') {
                            setTimeout(async () => {
                                // eslint-disable-next-line no-await-in-loop
                                const data = await visual.exportData(models.ExportDataType.Summarized);

                                // Split the data into rows
                                const rows = data.data.split('\r\n');

                                // Check if there are any data rows (more than just headers)
                                // Headers row + empty last row = 2, so we check for more than 2 rows
                                const hasData = rows.length > 2;
                                this.$emit('pie-chart-data-status', hasData);
                            }, 1000);
                        }

                        if (visual.type === 'clusteredColumnChart') {
                            setTimeout(async () => {
                                // eslint-disable-next-line no-await-in-loop
                                const data = await visual.exportData(models.ExportDataType.Summarized);
                                const match = data.data.match(/true|false/);
                                const booleanValue = match ? match[0] === 'true' : null;
                                this.$emit('clustered-column-chart-status', booleanValue);
                                return booleanValue;
                            }, 1000);
                        }

                        if (visual.type === 'donutChart') {
                            setTimeout(async () => {
                                // eslint-disable-next-line no-await-in-loop
                                const data = await visual.exportData(models.ExportDataType.Summarized);
                                const match = data.data.match(/true|false/);
                                const booleanValue = match ? match[0] === 'true' : null;
                                this.$emit('donut-chart-status', booleanValue);
                                return booleanValue;
                            }, 1000);
                        }
                    }
                } catch (error) {
                    this.$toast.error('Error getting report data', {
                        timeout: constants.domain.defaultToastTimeout,
                    });
                }
            }

            return null;
        },
    },
};
</script>

<style lang='scss' scoped>

.custom-loader-wrapper {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);

    .custom-loader {
        box-sizing: border-box;
        width: 50px;
        height: 50px;
        overflow: hidden;
        animation: spin 1s linear infinite;
        border: 8px solid #f3f3f3;
        border-top: 8px solid #008bfb;
        border-radius: 50%;
    }
}

.reports-page {
    height: 100%;

    &__container {
        height: calc(100vh - 175px);
        margin: 0 20px;
        padding: 10px;
        border-radius: 3px;
        background: $color-white;
        box-shadow: $box-shadow-inner-page;

        @include respond-to($media-mobile-nav, max) {
            height: calc(100vh - 122px);
        }

        @include respond-to($media-mobile, max) {
            height: calc(100vh - 40px);
            margin-right: 0;
            margin-left: 0;
            border-radius: 0;
        }

        .resizer {
            display: flex;
            height: 100%;
            margin: 0;
            padding: 0;
            overflow: hidden;
            resize: both;

            .report-spinner {
                margin: 20px 20px 0;
                border-radius: 3px;
            }

            .section-class {
                position: relative;
                flex-grow: 1;
                width: 100%;
                height: 100%;
                margin: 0;
                padding: 0;
                border: 0;

                ::v-deep iframe {
                    border: none;
                }
            }
        }
    }
}

.reports-page-custom {
    position: relative;
    height: 100%;

    &__container {
        height: calc(100vh - 175px);
        margin: 0 0 20px 30px;
        padding: 10px;
        border-radius: 3px;
        background: $color-white;
        box-shadow: $box-shadow-inner-page;

        @include respond-to($media-mobile-nav, max) {
            height: calc(100vh - 122px);
        }

        @include respond-to($media-mobile, max) {
            height: calc(100vh - 40px);
            margin-right: 0;
            margin-left: 0;
            border-radius: 0;
        }

        .report-title-header {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            width: 100%;
            height: 62px;
            padding: 12px 24px;
            border-bottom: 1px solid #d0d5dd;
        }

        .report-title-header > p {
            margin: 0;
            color: #101828;
            font-family: Calibri, sans-serif;
            font-size: 24px;
            font-weight: 500;
            line-height: 38px;
            text-align: left;
        }

        .resizer {
            display: flex;
            position: relative;
            height: 100%;
            margin: 0;
            padding: 0;
            overflow: hidden;
            resize: both;

            .report-spinner-custom {
                position: absolute;
                top: 18%;
                left: 50%;
                height: 20%;
                margin: 0 auto;
                transform: translate(-50%, -50%);
            }

            .section-class {
                position: relative;
                flex-grow: 1;
                width: 100%;
                height: 100%;
                margin: 0;
                padding: 0;
                border: 0;

                ::v-deep iframe {
                    border: none;
                }
            }
        }
    }
}

.tooltip-overlay {
    position: absolute;
    z-index: 1;
    width: 100%;
    height: 100%;
}

.one-warehouse-selected {
    display: flex;
    z-index: 2;
    align-items: center;
    justify-content: center;
    width: 100%;
    margin-right: auto;
    margin-left: auto;
    border-radius: 14px;
    background-color: #fff;
}

.one-warehouse-selected img {
    width: auto;
    height: 470px;
}

.custom-report-title-header {
    display: flex;
    flex-direction: column;
    flex-wrap: none;
    align-content: center;
    align-items: center;
    gap: 14px;
    width: 100%;
    padding-top: 30px;
    padding-bottom: 10px;
}

.custom-report-title {
    display: inline-block;
    position: relative;
    color: $color-gray-200;
    font-size: 24px;
    font-weight: 500;
}

.report-command-btn-section {
    display: flex;
    position: absolute;
    flex-direction: row;
    gap: 14px;
    margin-top: -30px;
    margin-left: 500px;
}

.report-command-btn {
    display: inline-block;
    align-content: center;
    width: 97px;
    border-width: 1px;
    border-style: solid;
    border-color: $color-gray-button;
    color: $color-gray-button;
    font-size: 14px;
    text-align: center;
    text-decoration: none;
}

.report-command-btn:disabled {
    border-color: $color-gray-600;
    color: $color-gray-600;
    cursor: not-allowed;
}

.report-command-btn:not(:disabled):hover {
    cursor: pointer;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}
</style>
